import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import "react-phone-number-input/style.css";
import { useAuth } from "../../api/hooks/useAuth";
import { useCart } from "../../context/CartContext";
import { useSession } from "../../context/SessionContext";
import { useToast } from "../../admin/context/ToastContext";
import StateSelector from "../../components/StateSelector/StateSelector";
import ForgotPasswordModal from "../../components/ForgotPasswordModal/ForgotPasswordModal";
import OTPVerificationModal from "../../components/OTPVerificationModal/OTPVerificationModal";
import ResetPasswordModal from "../../components/ResetPasswordModal/ResetPasswordModal";
import axiosInstance from "../../api/config/axios.config";
import ENDPOINTS from "../../api/config/endpoints";

const DEFAULT_PHONE_COUNTRY = "US";
const MAX_DOCUMENTS = 3;
const MAX_PHONE_DIGITS = 15;
const ID_PROOF_OPTIONS = [
  { value: "driving_license", label: "Driving License" },
  { value: "passport", label: "Passport" },
  { value: "state_issued_id", label: "State Issued ID" },
];

const createEmptyDocument = (id) => ({
  id,
  type: "",
  number: "",
  expiry: "",
  fileKey: "",
  fileName: "",
  fileUrl: "",
});

const REGION_NAME_FORMATTER =
  typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

const getCountryNameFromIso = (countryCode) => {
  if (!countryCode) return "";
  return REGION_NAME_FORMATTER?.of(countryCode) || countryCode;
};

const normalizePhoneInput = (value) => {
  const raw = String(value || "");
  const digits = raw.replace(/\D/g, "").slice(0, MAX_PHONE_DIGITS);
  return raw.trim().startsWith("+") ? `+${digits}` : digits;
};


export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, logout, loading, error, clearError, verifyOtp } = useAuth();
  const { showToast } = useToast();
  const { upgradeGuestCartToUser } = useCart();
  const { ipAddress } = useSession();
  const proofInputRefs = useRef({});

  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isOTPVerificationOpen, setIsOTPVerificationOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    mobile: "",
    phoneValue: "",
    country: "",
    state: "",
    role: "consumer",
    confirm_password: "",
  });
  const [documents, setDocuments] = useState([]);
  const [uploadingDocId, setUploadingDocId] = useState(null);

  const passwordRequirements = {
    hasUpper: /[A-Z]/.test(formData.password),
    hasLower: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  // Sync isLogin state with URL if needed
  useEffect(() => {
    if (location.pathname === "/signup" || location.pathname === "/register") {
      setIsLogin(false);
    } else {
      setIsLogin(true);
    }
  }, [location.pathname]);

  const handlePhoneChange = (value) => {
    setFormData((prev) => {
      const nextValue = normalizePhoneInput(value);
      const parsedPhone = parsePhoneNumberFromString(nextValue || "");
      return {
        ...prev,
        phoneValue: nextValue,
        mobile: parsedPhone?.nationalNumber || "",
        country: parsedPhone?.country ? getCountryNameFromIso(parsedPhone.country) : prev.country,
      };
    });
    if (error) clearError();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) clearError();
  };

  const updateDocumentField = (id, field, value) => {
    setDocuments((prev) =>
      prev.map((doc) => (doc.id === id ? { ...doc, [field]: value } : doc))
    );
  };

  const addDocument = () => {
    setDocuments((prev) => {
      if (prev.length >= MAX_DOCUMENTS) return prev;
      const nextId = prev.length > 0 ? Math.max(...prev.map((doc) => doc.id)) + 1 : 1;
      return [...prev, createEmptyDocument(nextId)];
    });
  };

  const removeDocument = (id) => {
    setDocuments((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((doc) => doc.id !== id);
    });
  };

  const openDocumentPicker = (doc) => {
    if (!doc.type) {
      showToast("Select document type first", "error");
      return;
    }
    proofInputRefs.current[doc.id]?.click();
  };

  const handleDocumentFileChange = async (docId, event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const formDataFile = new FormData();
    formDataFile.append("document", file);
    setUploadingDocId(docId);

    try {
      const res = await axiosInstance.post(ENDPOINTS.AUTH.UPLOAD_DOCUMENT, formDataFile, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const body = res.data;
      const filename =
        body?.data?.filename ||
        body?.filename ||
        body?.data?.file_name ||
        body?.file_name;
      const fileUrl =
        body?.data?.file_url ||
        body?.file_url ||
        body?.data?.document_url ||
        body?.document_url;
      const uploadedValue = filename || fileUrl;

      if (uploadedValue) {
        updateDocumentField(docId, "fileKey", uploadedValue);
        updateDocumentField(docId, "fileName", filename || file.name);
        updateDocumentField(docId, "fileUrl", fileUrl || uploadedValue);
        showToast("Document uploaded successfully", "success");
      } else {
        showToast(body?.message || "Upload succeeded but file data is missing", "warning");
      }
    } catch (err) {
      showToast(err?.response?.data?.message || err.message || "Failed to upload proof", "error");
    } finally {
      setUploadingDocId(null);
      if (proofInputRefs.current[docId]) {
        proofInputRefs.current[docId].value = "";
      }
    }
  };

  const handleToggleMode = (mode) => {
    clearError();
    if (mode === "login") {
      navigate("/login", { replace: true });
    } else if (mode === "register") {
      navigate("/signup", { replace: true });
    }
    setIsLogin(mode === "login");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let result;
      if (isLogin) {
        result = await login({
          email: formData.email,
          password: formData.password
        });
      } else {
        if (!isPasswordValid) {
          showToast("Please meet all password requirements", "error");
          return;
        }
        if (formData.password !== formData.confirm_password) {
          showToast("Passwords do not match", "error");
          return;
        }
        if (formData.mobile.length !== 10) {
          showToast("Please enter a valid 10-digit mobile number", "error");
          return;
        }

        const preparedDocuments = documents
          .map((doc) => ({
            ...doc,
            type: String(doc.type || "").trim(),
            number: String(doc.number || "").trim(),
          }))
          .filter((doc) => doc.type || doc.number || doc.fileKey);

        const incompleteDocument = preparedDocuments.find(
          (doc) => !doc.type || !doc.number || !doc.fileKey
        );

        if (incompleteDocument) {
          showToast("Complete each added document or remove the incomplete one", "error");
          return;
        }

        // Map each document type to the flat field names the backend expects.
        const documentFieldMap = {
          driving_license: { id: "driving_licence_id", file: "driving_licence_file" },
          passport: { id: "passport_id", file: "passport_file" },
          state_issued_id: { id: "state_issued_id", file: "state_issued_file" },
        };

        const registerPayload = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          password: formData.password,
          mobile: formData.phoneValue,
          role: formData.role,
          profile_pic: "",
          state: formData.state,
          country: formData.country,
          // Always send all document fields; unselected types are sent as empty strings.
          driving_licence_id: "",
          driving_licence_file: "",
          passport_id: "",
          passport_file: "",
          state_issued_id: "",
          state_issued_file: "",
        };

        preparedDocuments.forEach((doc) => {
          const mapping = documentFieldMap[doc.type];
          if (mapping) {
            registerPayload[mapping.id] = doc.number;
            registerPayload[mapping.file] = doc.fileKey || "";
          }
        });

        result = await register(registerPayload);
      }

      // Check if user is an admin - we should not allow admin to login on user side
      if (result.user?.role === "admin") {
        await logout();
        showToast("Access denied. Please use the admin login page.", "error");
        return;
      }

      // If registration was successful (even without user data in response)
      if (result.success && !isLogin) {
        showToast(result.message || "Registration successful! Please sign in to continue.", "success");
        setIsLogin(true);
        navigate("/login", { replace: true });
        
        // Clear all form fields after successful registration
        setFormData({
          email: "",
          password: "",
          first_name: "",
          last_name: "",
          mobile: "",
          phoneValue: "",
          country: "",
          state: "",
          role: "consumer",
          confirm_password: "",
        });
        setDocuments([]);
        return;
      }

      if (result.user?.role === "user" || result.user?.role === "consumer") {
        if (isLogin) {
          showToast(result.message || "Login successful!", "success");

          // After login, upgrade any guest cart (by ip_address) to this user_uuid
          await upgradeGuestCartToUser({
            user_uuid: result.user?.uuid,
            ip_address: ipAddress,
          });

          // Redirect back to previous page (e.g., /checkout) if provided, else home
          const redirectTo = location.state?.redirectTo || "/";
          navigate(redirectTo, { replace: true });
        }
      }
    } catch (err) {
      console.error(`${isLogin ? "Login" : "Sign Up"} error:`, err);
      showToast(err.message || `${isLogin ? "Login" : "Sign Up"} failed`, "error");
    }
  };

  const handleForgotPasswordSuccess = (email) => {
    setResetEmail(email);
    setIsForgotPasswordOpen(false);
    setIsOTPVerificationOpen(true);
  };

  const handleVerifySuccess = async (otp) => {
    try {
      const result = await verifyOtp(resetEmail, otp);
      if (result.status || result.success) {
        showToast("OTP Verified successfully!", "success");
        setIsOTPVerificationOpen(false);
        setIsResetPasswordOpen(true);
      } else {
        showToast(result.message || "Invalid OTP", "error");
      }
    } catch (err) {
      showToast(err.message || "OTP verification failed", "error");
    }
  };

  const handleResetSuccess = () => {
    setIsResetPasswordOpen(false);
    showToast("Password reset successful! Please login with your new password.", "success");
    setIsLogin(true);
  };

  return (
    <div style={{
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "var(--bg-primary, #F8FAFC)",
      padding: "20px",
      fontFamily: "var(--font-body, sans-serif)",
      boxSizing: "border-box",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch"
    }}>
      <style>
        {`
          @media (max-height: 800px) {
            .auth-card { margin-top: 40px !important; margin-bottom: 40px !important; }
          }
          @media (max-width: 480px) {
            .auth-card { padding: 24px 20px !important; border-radius: 24px !important; }
            .auth-logo { height: 100px !important; }
            .auth-title { font-size: 24px !important; }
          }
          /* PhoneInput specific styling */
          .PhoneInput {
            width: 100% !important;
            padding: 12px 16px !important;
            border-radius: 14px !important;
            border: 1px solid #E5E7EB !important;
            font-size: 14px !important;
            outline: none !important;
            transition: all 0.2s !important;
            background-color: #F9FAFB !important;
          }
          .PhoneInput--focus {
            border-color: var(--accent-primary, #00B8D9) !important;
            background-color: #FFFFFF !important;
            box-shadow: 0 0 0 4px rgba(0, 184, 217, 0.1) !important;
          }
          .PhoneInputInput {
            border: none !important;
            background: transparent !important;
            outline: none !important;
            font-size: 14px !important;
          }
          .PhoneInputCountry {
            border: none !important;
            background: transparent !important;
          }
        `}
      </style>
      {/* Main Card */}
      <div
        className="auth-card"
        style={{
          width: "100%",
          maxWidth: isLogin ? "480px" : "560px",
          backgroundColor: "var(--bg-card, #FFFFFF)",
          borderRadius: "var(--radius-4xl, 32px)",
          boxShadow: "var(--shadow-premium, 0 20px 60px rgba(15, 23, 42, 0.08))",
          padding: "32px 24px",
          border: "1px solid var(--glass-border, rgba(229, 231, 235, 0.5))",
          margin: "auto",
          position: "relative",
          zIndex: 1
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          {/* Logo inside card */}
          <div style={{ marginBottom: "8px" }}>
            <Link to="/" style={{ display: "inline-block" }}>
              <img
                className="auth-logo"
                src="/chilltree-images/chilltree-logo1.png"
                alt="Chilltree"
                style={{
                  height: "80px",
                  width: "auto",
                  objectFit: "contain",
                  transition: "height 0.3s"
                }}
              />
            </Link>
          </div>

          <h1
            className="auth-title"
            style={{
              fontSize: "32px",
              fontWeight: "bold",
              color: "var(--text-heading, #0F172A)",
              marginBottom: "4px",
              fontFamily: "var(--font-display, serif)",
              transition: "font-size 0.3s"
            }}
          >
            Welcome <span style={{ color: "var(--accent-primary, #00B8D9)" }}>{isLogin ? "Back" : "to Chilltree"}</span>
          </h1>
          <p style={{ color: "var(--text-body, #475569)", fontSize: "14px" }}>
            {isLogin
              ? "Sign in to manage your orders and profile"
              : "Join us and start your journey"}
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: "flex",
          backgroundColor: "var(--bg-secondary, #F1F5F9)",
          padding: "4px",
          borderRadius: "999px",
          marginBottom: "32px",
          maxWidth: "280px",
          marginLeft: "auto",
          marginRight: "auto",
          position: "relative"
        }}>
          <button
            onClick={() => handleToggleMode("login")}
            style={{
              flex: 1,
              padding: "10px 24px",
              borderRadius: "999px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              backgroundColor: isLogin ? "#FFFFFF" : "transparent",
              color: isLogin ? "var(--text-heading, #0F172A)" : "var(--text-muted, #94A3B8)",
              boxShadow: isLogin ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" : "none",
              zIndex: 1
            }}
          >
            Login
          </button>
          <button
            onClick={() => handleToggleMode("register")}
            style={{
              flex: 1,
              padding: "10px 24px",
              borderRadius: "999px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              backgroundColor: !isLogin ? "#FFFFFF" : "transparent",
              color: !isLogin ? "var(--text-heading, #0F172A)" : "var(--text-muted, #94A3B8)",
              boxShadow: !isLogin ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" : "none",
              zIndex: 1
            }}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {!isLogin && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-body, #475569)", marginLeft: "4px" }}>First name *</label>
                <input
                  type="text"
                  name="first_name"
                  placeholder="First name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "14px",
                    border: "1px solid #E5E7EB",
                    fontSize: "14px",
                    outline: "none",
                    transition: "all 0.2s",
                    backgroundColor: "#F9FAFB"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--accent-primary, #00B8D9)";
                    e.target.style.backgroundColor = "#FFFFFF";
                    e.target.style.boxShadow = "0 0 0 4px rgba(0, 184, 217, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#E5E7EB";
                    e.target.style.backgroundColor = "#F9FAFB";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-body, #475569)", marginLeft: "4px" }}>Last name *</label>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "14px",
                    border: "1px solid #E5E7EB",
                    fontSize: "14px",
                    outline: "none",
                    transition: "all 0.2s",
                    backgroundColor: "#F9FAFB"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--accent-primary, #00B8D9)";
                    e.target.style.backgroundColor = "#FFFFFF";
                    e.target.style.boxShadow = "0 0 0 4px rgba(0, 184, 217, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#E5E7EB";
                    e.target.style.backgroundColor = "#F9FAFB";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginLeft: "4px" }}>
              {isLogin ? "Email" : "Email address *"}
            </label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", display: "flex", alignItems: "center" }}>
                <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "18px", height: "18px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </span>
              <input
                type={isLogin ? "text" : "email"}
                name="email"
                placeholder={isLogin ? "Email or Mobile Number" : "Enter your email"}
                value={formData.email}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "12px 16px 12px 48px",
                  borderRadius: "14px",
                  border: "1px solid #E5E7EB",
                  fontSize: "14px",
                  outline: "none",
                  transition: "all 0.2s",
                  backgroundColor: "#F9FAFB"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#A855F7";
                  e.target.style.backgroundColor = "#FFFFFF";
                  e.target.style.boxShadow = "0 0 0 4px rgba(168, 85, 247, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E5E7EB";
                  e.target.style.backgroundColor = "#F9FAFB";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {!isLogin && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginLeft: "4px" }}>Mobile number *</label>
              <PhoneInput
                id="mobile"
                international
                defaultCountry={DEFAULT_PHONE_COUNTRY}
                countryCallingCodeEditable={false}
                // Trims typing at the selected country's real maximum, so a US
                // number stops at 10 national digits instead of running on to
                // the 15-digit global E.164 ceiling
                limitMaxLength
                placeholder="Enter mobile number"
                value={normalizePhoneInput(formData.phoneValue)}
                onChange={handlePhoneChange}
                required
              />
            </div>
          )}
          {!isLogin && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-body, #475569)", marginLeft: "4px" }}>Country</label>
                <input
                  id="country"
                  name="country"
                  type="text"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "14px",
                    border: "1px solid #E5E7EB",
                    fontSize: "14px",
                    outline: "none",
                    transition: "all 0.2s",
                    backgroundColor: "#F9FAFB"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--accent-primary, #00B8D9)";
                    e.target.style.backgroundColor = "#FFFFFF";
                    e.target.style.boxShadow = "0 0 0 4px rgba(0, 184, 217, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#E5E7EB";
                    e.target.style.backgroundColor = "#F9FAFB";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-body, #475569)", marginLeft: "4px" }}>State</label>
                <StateSelector
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "14px",
                    border: "1px solid #E5E7EB",
                    fontSize: "14px",
                    outline: "none",
                    transition: "all 0.2s",
                    backgroundColor: "#F9FAFB"
                  }}
                />
              </div>
            </div>
          )}

          {!isLogin && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "14px",
                padding: "16px",
                borderRadius: "18px",
                border: "1px solid #E5E7EB",
                backgroundColor: "#F8FAFC",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginLeft: "4px" }}>
                    Identification Documents
                  </label>
                  <p style={{ margin: "6px 0 0 4px", fontSize: "12px", color: "#64748B", lineHeight: 1.5 }}>
                    Add up to 3 documents. Fill type, number and upload file.
                  </p>
                </div>
                {documents.length < MAX_DOCUMENTS && (
                  <button
                    type="button"
                    onClick={addDocument}
                    disabled={loading || uploadingDocId !== null}
                    style={{
                      minHeight: "36px",
                      padding: "0 14px",
                      borderRadius: "10px",
                      border: "1px solid #00B8D9",
                      backgroundColor: "#FFFFFF",
                      color: "#0284C7",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: loading || uploadingDocId !== null ? "not-allowed" : "pointer",
                      opacity: loading || uploadingDocId !== null ? 0.65 : 1,
                    }}
                  >
                    + Add Document
                  </button>
                )}
              </div>

              {documents.map((doc, index) => (
                <div
                  key={doc.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    padding: "14px",
                    borderRadius: "14px",
                    border: "1px solid #E5E7EB",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        minHeight: "28px",
                        padding: "0 10px",
                        borderRadius: "999px",
                        backgroundColor: "rgba(0, 184, 217, 0.08)",
                        color: "#0284C7",
                        fontSize: "12px",
                        fontWeight: "700",
                      }}
                    >
                      Document {index + 1}
                    </span>
                    {documents.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDocument(doc.id)}
                        disabled={loading || uploadingDocId !== null}
                        style={{
                          minHeight: "30px",
                          padding: "0 10px",
                          borderRadius: "8px",
                          border: "1px solid rgba(239, 68, 68, 0.2)",
                          backgroundColor: "rgba(239, 68, 68, 0.06)",
                          color: "#DC2626",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: loading || uploadingDocId !== null ? "not-allowed" : "pointer",
                          opacity: loading || uploadingDocId !== null ? 0.65 : 1,
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginLeft: "4px" }}>
                        Document type
                      </label>
                      <select
                        value={doc.type}
                        onChange={(e) => updateDocumentField(doc.id, "type", e.target.value)}
                        disabled={loading || uploadingDocId !== null}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          borderRadius: "12px",
                          border: "1px solid #E5E7EB",
                          fontSize: "14px",
                          outline: "none",
                          backgroundColor: "#F9FAFB",
                          cursor: loading || uploadingDocId !== null ? "not-allowed" : "pointer",
                        }}
                      >
                        <option value="">Select type</option>
                        {ID_PROOF_OPTIONS.filter(
                          (option) =>
                            option.value === doc.type ||
                            !documents.some((d) => d.id !== doc.id && d.type === option.value)
                        ).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginLeft: "4px" }}>
                        Document number
                      </label>
                      <input
                        type="text"
                        value={doc.number}
                        onChange={(e) => updateDocumentField(doc.id, "number", e.target.value)}
                        disabled={loading || uploadingDocId !== null}
                        placeholder="Enter number"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          borderRadius: "12px",
                          border: "1px solid #E5E7EB",
                          fontSize: "14px",
                          outline: "none",
                          backgroundColor: "#F9FAFB",
                        }}
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginLeft: "4px" }}>
                        Upload file
                      </label>
                      <button
                        type="button"
                        onClick={() => openDocumentPicker(doc)}
                        disabled={loading || uploadingDocId === doc.id}
                        style={{
                          width: "100%",
                          minHeight: "46px",
                          padding: "0 16px",
                          borderRadius: "12px",
                          border: "1px solid #00B8D9",
                          backgroundColor: "#FFFFFF",
                          color: "#0284C7",
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: loading || uploadingDocId === doc.id ? "not-allowed" : "pointer",
                          opacity: loading || uploadingDocId === doc.id ? 0.65 : 1,
                        }}
                      >
                        {uploadingDocId === doc.id ? "Uploading..." : doc.fileName ? "Change File" : "Choose File"}
                      </button>
                      <input
                        ref={(el) => {
                          proofInputRefs.current[doc.id] = el;
                        }}
                        type="file"
                        accept=".pdf,image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleDocumentFileChange(doc.id, e)}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                      flexWrap: "wrap",
                      padding: "10px 12px",
                      borderRadius: "12px",
                      backgroundColor: "#F8FAFC",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <span style={{ fontSize: "13px", color: "#64748B", wordBreak: "break-word" }}>
                      {doc.fileName || "No file uploaded yet"}
                    </span>
                    {doc.fileUrl && (
                      <a
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: "#0284C7",
                          fontSize: "13px",
                          fontWeight: "700",
                          textDecoration: "none",
                        }}
                      >
                        View
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLogin && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginLeft: "4px" }}>Role *</label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", display: "flex", alignItems: "center", zIndex: 1 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "18px", height: "18px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </span>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px 16px 12px 48px",
                    borderRadius: "14px",
                    border: "1px solid #E5E7EB",
                    fontSize: "14px",
                    outline: "none",
                    transition: "all 0.2s",
                    backgroundColor: "#F9FAFB",
                    appearance: "none",
                    cursor: "pointer"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--accent-primary, #00B8D9)";
                    e.target.style.backgroundColor = "#FFFFFF";
                    e.target.style.boxShadow = "0 0 0 4px rgba(0, 184, 217, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#E5E7EB";
                    e.target.style.backgroundColor = "#F9FAFB";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <option value="consumer">Consumer</option>
                </select>
                <span style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", pointerEvents: "none" }}>
                  ▼
                </span>
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 4px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-body, #475569)" }}>Password *</label>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => setIsForgotPasswordOpen(true)}
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "var(--accent-primary, #00B8D9)",
                    textDecoration: "none",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0
                  }}
                >
                  Forgot password?
                </button>
              )}
            </div>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", display: "flex", alignItems: "center" }}>
                <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "18px", height: "18px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </span>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder={isLogin ? "Enter password" : "Create password"}
                value={formData.password}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "12px 48px 12px 48px",
                  borderRadius: "14px",
                  border: "1px solid #E5E7EB",
                  fontSize: "14px",
                  outline: "none",
                  transition: "all 0.2s",
                  backgroundColor: "#F9FAFB"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#A855F7";
                  e.target.style.backgroundColor = "#FFFFFF";
                  e.target.style.boxShadow = "0 0 0 4px rgba(168, 85, 247, 0.1)";
                  setIsPasswordFocused(true);
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E5E7EB";
                  e.target.style.backgroundColor = "#F9FAFB";
                  e.target.style.boxShadow = "none";
                  setIsPasswordFocused(false);
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "16px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9CA3AF",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "18px", height: "18px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "18px", height: "18px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                )}
              </button>
            </div>
            {!isLogin && isPasswordFocused && (
              <div style={{
                marginTop: "8px",
                padding: "12px",
                backgroundColor: "#F9FAFB",
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "11px",
                  color: passwordRequirements.hasUpper ? "#10B981" : "#64748B",
                  fontWeight: passwordRequirements.hasUpper ? "600" : "400",
                  transition: "all 0.2s"
                }}>
                  <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: passwordRequirements.hasUpper ? "#10B981" : "#9CA3AF" }} />
                  One uppercase
                </div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "11px",
                  color: passwordRequirements.hasLower ? "#10B981" : "#64748B",
                  fontWeight: passwordRequirements.hasLower ? "600" : "400",
                  transition: "all 0.2s"
                }}>
                  <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: passwordRequirements.hasLower ? "#10B981" : "#9CA3AF" }} />
                  One lowercase
                </div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "11px",
                  color: passwordRequirements.hasNumber ? "#10B981" : "#64748B",
                  fontWeight: passwordRequirements.hasNumber ? "600" : "400",
                  transition: "all 0.2s"
                }}>
                  <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: passwordRequirements.hasNumber ? "#10B981" : "#9CA3AF" }} />
                  One number
                </div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "11px",
                  color: passwordRequirements.hasSpecial ? "#10B981" : "#64748B",
                  fontWeight: passwordRequirements.hasSpecial ? "600" : "400",
                  transition: "all 0.2s"
                }}>
                  <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: passwordRequirements.hasSpecial ? "#10B981" : "#9CA3AF" }} />
                  One special character
                </div>
              </div>
            )}
          </div>

          {!isLogin && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginLeft: "4px" }}>Confirm Password *</label>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: "16px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", display: "flex", alignItems: "center" }}>
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "18px", height: "18px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirm_password"
                  placeholder="Confirm your password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "12px 48px 12px 48px",
                    borderRadius: "14px",
                    border: "1px solid #E5E7EB",
                    fontSize: "14px",
                    outline: "none",
                    transition: "all 0.2s",
                    backgroundColor: "#F9FAFB"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--accent-primary, #00B8D9)";
                    e.target.style.backgroundColor = "#FFFFFF";
                    e.target.style.boxShadow = "0 0 0 4px rgba(0, 184, 217, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#E5E7EB";
                    e.target.style.backgroundColor = "#F9FAFB";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#9CA3AF",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  {showConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "18px", height: "18px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "18px", height: "18px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px",
              background: "var(--gradient-primary, linear-gradient(135deg, #00B8D9 0%, #0284C7 100%))",
              color: "#FFFFFF",
              borderRadius: "14px",
              fontSize: "16px",
              fontWeight: "600",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "all 0.3s",
              marginTop: "12px",
              opacity: loading ? 0.7 : 1,
              boxShadow: "var(--shadow-accent, 0 0 30px rgba(0, 184, 217, 0.15))"
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "var(--shadow-hover, 0 25px 80px rgba(0, 184, 217, 0.12))";
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "var(--shadow-accent, 0 0 30px rgba(0, 184, 217, 0.15))";
              }
            }}
          >
            {loading ? (isLogin ? "Signing in..." : "Setting up...") : (isLogin ? "Sign In" : "Sign Up")}
          </button>
        </form>

        <div style={{ marginTop: "32px", paddingTop: "24px", borderTop: "1px solid #F3F4F6", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#64748B" }}>
              <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "16px", height: "16px", color: "#10B981" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Secure
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#64748B" }}>
              <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "16px", height: "16px", color: "#10B981" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Private
            </div>
          </div>
          <p style={{ fontSize: "11px", color: "#9CA3AF" }}>
            By continuing, you agree to our Terms & Privacy Policy.
          </p>
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
        onSuccess={handleForgotPasswordSuccess}
      />

      <OTPVerificationModal
        isOpen={isOTPVerificationOpen}
        onClose={() => setIsOTPVerificationOpen(false)}
        email={resetEmail}
        onVerifySuccess={handleVerifySuccess}
      />

      <ResetPasswordModal
        isOpen={isResetPasswordOpen}
        onClose={() => setIsResetPasswordOpen(false)}
        email={resetEmail}
        onResetSuccess={handleResetSuccess}
      />
    </div>
  );
}



